// The route transition: 180ms fade + 8px rise, on the content region only. The shell -
// sidebar, header, nav - never moves. One shot per navigation, nothing loops, and under
// `prefers-reduced-motion` nothing runs at all.
import * as React from "react";
import { useLocation } from "react-router";
import { animateOnce, DURATIONS, RISE_IN } from "@/lib/motion";
import { cn } from "@/lib/utils";

type PageTransitionProps = React.ComponentProps<"div">;

/**
 * Wrap the routed content with this, inside the shell:
 *
 *     <PageTransition><Outlet /></PageTransition>
 *
 * The `key` remounts the content on every path change, so a screen never animates its
 * old DOM. The animation itself is WAAPI, not a CSS class, so it cannot be left running.
 */
export function PageTransition({ className, children, ...props }: PageTransitionProps) {
  const { pathname } = useLocation();
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    animateOnce(ref.current, RISE_IN, { duration: DURATIONS.fast });
  }, [pathname]);

  return (
    <div
      key={pathname}
      ref={ref}
      data-slot="page-transition"
      className={cn("min-w-0", className)}
      {...props}
    >
      {children}
    </div>
  );
}
